// src/commands/create.ts

import { join } from "path"
import prompts from "prompts"
import { ProjectBuilder } from "../core/project-builder.js"
import { loadPreset } from "../presets/index.js"
import { getPrompts } from "../prompts/index.js"
import { validateProjectName } from "../prompts/validators.js"
import type { CLIOptions, ProjectConfig } from "../types/types.js"
import { CLIError } from "../types/types.js"
import { logger } from "../utils/logger.js"

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: For now this is okay
export async function create(
  projectName: string | undefined,
  options: CLIOptions,
): Promise<void> {
  try {
    logger.box("Frontend Setup Wizard", [
      "Create modern web applications with your preferred stack",
      "Version 2.0.0",
    ])
    logger.newLine()

    // Configure logger based on options
    if (options.debug) {
      logger.setDebugMode(true)
      logger.debug("Debug mode enabled")
    }

    // Load preset if specified
    let presetConfig: Partial<ProjectConfig> = {}
    if (options.preset) {
      presetConfig = loadPreset(options.preset)
      logger.info(`Using preset: ${options.preset}`)
    }

    // Validate project name if provided
    if (projectName) {
      const validation = validateProjectName(projectName)
      if (validation !== true) {
        logger.error(validation as string)
        process.exit(1)
      }
    }

    // Get user input through prompts
    const promptQuestions = getPrompts(projectName, options, presetConfig)

    if (promptQuestions.length === 0 && !projectName) {
      logger.error("Project name is required")
      process.exit(1)
    }

    const responses =
      promptQuestions.length > 0
        ? await prompts(promptQuestions, {
            onCancel: () => {
              logger.warning("Setup cancelled")
              process.exit(0)
            },
          })
        : {}

    // Build final configuration
    const config: ProjectConfig = {
      codeQuality:
        options.quality ||
        responses.codeQuality ||
        presetConfig.codeQuality ||
        "biome",
      debug: options.debug || false,
      dryRun: options.dryRun || false,
      framework:
        options.framework ||
        responses.framework ||
        presetConfig.framework ||
        "react",
      gitHooks:
        options.gitHooks ??
        responses.gitHooks ??
        presetConfig.gitHooks ??
        false,
      metaFramework:
        options.meta ||
        responses.metaFramework ||
        presetConfig.metaFramework ||
        "none",
      packageManager:
        options.pm ||
        responses.packageManager ||
        presetConfig.packageManager ||
        "npm",
      projectName: projectName || responses.projectName,
      projectPath: join(process.cwd(), projectName || responses.projectName),
      tailwind:
        options.tailwind ?? responses.tailwind ?? presetConfig.tailwind ?? true,
      typescript:
        options.typescript ??
        responses.typescript ??
        presetConfig.typescript ??
        true,
    }

    if (config.dryRun) {
      logger.warning("Running in dry-run mode - no files will be created")
      logger.newLine()
    }

    // Show configuration summary
    showConfigSummary(config)

    // Confirm before proceeding
    if (!config.dryRun) {
      const { confirm } = await prompts({
        initial: true,
        message: "Create project with this configuration?",
        name: "confirm",
        type: "confirm",
      })

      if (!confirm) {
        logger.warning("Setup cancelled")
        process.exit(0)
      }
    }

    logger.newLine()

    // Build the project
    const builder = new ProjectBuilder(config)
    await builder.build()

    // Show success message and next steps
    showSuccessMessage(config)
  } catch (error) {
    if (error instanceof CLIError) {
      logger.error(`Setup failed: ${error.message}`)
      if (error.details && options.debug) {
        console.error("Error details:", error.details)
      }
    } else if (error instanceof Error) {
      logger.error(`Setup failed: ${error.message}`)
      if (options.debug && error.stack) {
        console.error(error.stack)
      }
    } else {
      logger.error("Setup failed: Unknown error")
      if (options.debug) {
        console.error(error)
      }
    }
    process.exit(1)
  }
}

function showConfigSummary(config: ProjectConfig): void {
  logger.divider()
  logger.info("Configuration Summary:")
  logger.newLine()

  const summary: Array<[string, string]> = [
    ["Project Name", config.projectName],
    ["Framework", getFrameworkDisplay(config.framework, config.metaFramework)],
    ["TypeScript", config.typescript ? "Yes" : "No"],
    ["Tailwind CSS", config.tailwind ? "Yes (v4)" : "No"],
    ["Package Manager", config.packageManager],
    [
      "Code Quality",
      config.codeQuality === "biome" ? "Biome" : "ESLint + Prettier",
    ],
    ["Git Hooks", config.gitHooks ? "Yes (Lefthook)" : "No"],
  ]

  logger.table(summary)
  logger.divider()
  logger.newLine()
}

function getFrameworkDisplay(framework: string, metaFramework: string): string {
  const frameworkNames: Record<string, string> = {
    astro: "Astro",
    react: "React",
    svelte: "Svelte",
    vanilla: "Vanilla JS",
    vue: "Vue",
  }

  const metaFrameworkNames: Record<string, string> = {
    next: "Next.js",
    none: "",
    nuxt: "Nuxt",
    remix: "Remix",
    sveltekit: "SvelteKit",
  }

  const base = frameworkNames[framework] || framework
  const meta = metaFrameworkNames[metaFramework] || ""

  return meta || base
}

function showSuccessMessage(config: ProjectConfig): void {
  logger.newLine()
  logger.box("🎉 Project Created Successfully!", [
    `Your ${getFrameworkDisplay(config.framework, config.metaFramework)} project is ready.`,
    "",
    "Next steps:",
    `  1. cd ${config.projectName}`,
    `  2. ${config.packageManager} ${config.packageManager === "npm" ? "run " : ""}dev`,
    "",
    "Happy coding! 🚀",
  ])
  logger.newLine()

  // Additional tips based on configuration
  if (config.tailwind) {
    logger.info(
      "💡 Tailwind CSS v4 is configured with modern CSS variables in globals.css",
    )
  }

  if (config.gitHooks) {
    logger.info('💡 Git hooks are configured. Run "git init" to activate them')
  }

  if (config.codeQuality === "biome") {
    logger.info(
      `💡 Use "${config.packageManager} run check" to lint and format your code`,
    )
  }

  logger.newLine()
}
